import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirebaseError } from 'firebase/app';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  COURTESY_GRANT_CATEGORIES,
  ALL_TENANT_KINDS,
  Country,
  localeForCountry,
  PhoneFormatter,
  TaxIdValidator,
  TenantLocaleSchema,
  timeZonesForCountry,
  type LifetimeFreeCategory,
  type TenantKind,
  type TenantLocale,
} from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { createCourtesyTenantCallable, validateTaxIdCallable, wasReportedByCallable } from '@/lib/callables';

const TAG = 'admin/create-courtesy-tenant';

/**
 * Países ofertados: os que têm máscara de telefone, validador de tax-id e
 * fusos embutidos, que é o que este formulário precisa.
 */
const COUNTRIES: readonly Country[] = [Country.BR, Country.MX, Country.US, Country.CA, Country.IT];

type Props = {
  open: boolean;
  /** Email do super-admin logado — vira o OWNER do tenant. Read-only no form. */
  ownerEmail: string;
  onClose: () => void;
  onCreated: (tenantId: string) => void;
};

type FormState = {
  tenantName: string;
  tenantKind: TenantKind;
  countryCode: Country;
  taxId: string;
  digits: string;
  locale: TenantLocale;
  timeZone: string;
  address: string;
  category: LifetimeFreeCategory;
  reason: string;
};

/** Formulário vazio para um país: idioma e fuso derivados dele. */
function initialState(country: Country): FormState {
  return {
    tenantName: '',
    tenantKind: 'RESTAURANT',
    countryCode: country,
    taxId: '',
    digits: '',
    locale: localeForCountry(country),
    timeZone: timeZonesForCountry(country)[0] ?? 'UTC',
    address: '',
    category: 'FOUNDER',
    reason: '',
  };
}

/**
 * Issue #120 — cria um tenant de **cortesia** com o super-admin logado como
 * OWNER, sem passar pela contratação (venda assistida do CRM). Coleta os
 * campos que o onboarding self-serve coletava — o onboarding saiu dos
 * clientes em 2026-09-24 (#714, #679).
 *
 * O submit chama só `createCourtesyTenant`, que cria tenant + member OWNER +
 * subscription já `commercial_mode: COURTESY`, com a categoria e o motivo
 * (#679 F7, #738). É o tipo comercial que libera o gate — até a F7 havia um
 * segundo callable, `applyLifetimeFree`, que criava uma assinatura Stripe com
 * cupom de 100 % só para o tenant ter um `sub_…`, e que podia falhar depois de
 * o tenant existir.
 *
 * Origem: #120 — commit 6fde07e94, 2026-07-10 12:54 +0200.
 *
 * Contrato: dois passos. `requestConfirm` valida o tax-id no servidor
 * (`validateTaxId`) e só então abre o `AdminConfirm`; `submit` chama
 * `createCourtesyTenant` e entrega o `tenantId` criado a `onCreated` (a
 * página navega para o detalhe). Erros: `already-exists` e `invalid-argument`
 * viram mensagem própria; o resto, genérica. Desde a #1005 o `already-exists`
 * é só colisão do id `{CC}-{autoId}` (o documento fiscal pode se repetir entre
 * tenants) — a mensagem pede para tentar de novo, não acusa o documento
 * (corrigido na auditoria de 2026-10-19 17:03 +0200, #1005).
 *
 * Usado por: `routes/TenantsPage.tsx` (botão "Nova empresa de cortesia" do
 * `TenantList`). Spec: `docs/screens/admin-tenant-create.md`.
 *
 * Arquitetura: o país escolhido aqui vira `tenant.countryCode`, imutável — o
 * telefone do OWNER usa a máscara desse país e o callable recusa telefone de
 * outro país (`resolveCountryFromPhone` ≠ `countryCode`), regra "Telefone —
 * só do país do tenant" do CLAUDE.md. Só super-admin com capability
 * ROOT/FINANCE passa no callable (#795, ADM-7).
 *
 * Dados — input de `createCourtesyTenant` (`CreateCourtesyTenantInput` em
 * `@etiquetachef/types`):
 *   { "tenantName": "Restaurante Exemplo", "tenantKind": "RESTAURANT",
 *     "countryCode": "BR", "taxId": "00000000000000",
 *     "phoneE164": "+5511900000000", "locale": "pt-BR",
 *     "timeZone": "America/Sao_Paulo", "address": "", "category": "FOUNDER",
 *     "reason": "" }
 * `reason` é obrigatório só com `category: OTHER`. Saída: `{ tenantId }`.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-25 (#926, achados; #1021): os dois `catch` só faziam
 *    `console.error` além da mensagem na tela — sem report. Hoje cada um chama
 *    `reportNonFatal` (`admin_courtesy_validate_tax_id_failed` /
 *    `admin_courtesy_create_tenant_failed`), sem os dados digitados.
 *  - 2026-10-25 (#1021): o erro ia só por toast, atrás do overlay do Modal
 *    (mesmo achado do CRM, #174); hoje é `Alert` no fim do formulário. Erro do
 *    `createCourtesyTenant` fecha o `AdminConfirm` e reabre este modal, onde o
 *    `Alert` aparece.
 *
 * Issues: #120 (cortesia pelo admin), #738 (cortesia como modo comercial),
 * #795 (ADM-7, capability), #714 (onboarding fora do web), #1021 (erro no modal).
 *
 * Histórico:
 *  - 2026-10-25 14:29 +0100 — (#1021) erro em `Alert` dentro do modal.
 *  - 2026-10-25 15:00 +0100 — (#1021) `reportNonFatal` nos dois `catch`.
 */
export function CreateCourtesyTenantDialog({
  open,
  ownerEmail,
  onClose,
  onCreated,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => initialState(Country.BR));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Erro inline do documento fiscal (DV/comprimento), como no onboarding. */
  const [taxIdError, setTaxIdError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(initialState(Country.BR));
    setConfirmOpen(false);
    setSubmitting(false);
    setError(null);
    setTaxIdError(null);
  }, [open]);

  const phoneSpec = useMemo(() => PhoneFormatter.specFor(form.countryCode), [form.countryCode]);
  const taxIdSpec = useMemo(() => TaxIdValidator.specFor(form.countryCode), [form.countryCode]);

  const countryOptions = useMemo<SelectProps.Option[]>(
    () =>
      COUNTRIES.map((c) => ({
        value: c,
        label: `${t(`country.${c}`)} (${PhoneFormatter.dialCodeFor(c)})`,
      })),
    [t],
  );

  const kindOptions = useMemo<SelectProps.Option[]>(
    () =>
      ALL_TENANT_KINDS.map((k) => ({
        value: k,
        label: t(`tenants.kind.${k}`, { defaultValue: k }),
      })),
    [t],
  );

  const localeOptions = useMemo<SelectProps.Option[]>(
    () => TenantLocaleSchema.options.map((l) => ({ value: l, label: l })),
    [],
  );

  const timeZoneOptions = useMemo<SelectProps.Option[]>(
    () => timeZonesForCountry(form.countryCode).map((tz) => ({ value: tz, label: tz })),
    [form.countryCode],
  );

  function patch(next: Partial<FormState>): void {
    setForm((prev) => ({ ...prev, ...next }));
    setError(null);
  }

  /** Trocar o país invalida telefone, tax-id, idioma e fuso — todos derivam dele. */
  function changeCountry(country: Country): void {
    setForm((prev) => ({
      ...prev,
      countryCode: country,
      taxId: '',
      digits: '',
      locale: localeForCountry(country),
      timeZone: timeZonesForCountry(country)[0] ?? 'UTC',
    }));
    setError(null);
    setTaxIdError(null);
  }

  /**
   * Mesmo contrato do antigo onboarding web (`Onboarding.tsx`, removido em
   * `faac6255d`, #714): o estado guarda o valor
   * LIMPO (`cleanInput` filtra caracteres e capa o comprimento) e o input exibe
   * `spec.format(...)`. A validação inline (DV, comprimento) é feedback imediato;
   * o gate autoritativo continua sendo o callable `validateTaxId` no submit.
   */
  function changeTaxId(raw: string): void {
    const cleaned = TaxIdValidator.cleanInput(form.countryCode, raw);
    setForm((prev) => ({ ...prev, taxId: cleaned }));
    setTaxIdError(taxIdSpec.validate(cleaned)?.message ?? null);
    setError(null);
  }

  const phoneComplete = form.digits.length === phoneSpec.expectedDigits;
  const canSubmit =
    form.tenantName.trim().length >= 2 &&
    form.taxId.trim().length >= 3 &&
    taxIdError === null &&
    phoneComplete &&
    form.timeZone.length > 0 &&
    (form.category !== 'OTHER' || form.reason.trim().length > 0);

  /**
   * Tax-id valida SEMPRE via Cloud Function (owner 2026-06-11) — o validador
   * compilado do cliente não é a fonte da verdade. Inválido bloqueia o submit.
   */
  async function requestConfirm(): Promise<void> {
    if (form.category === 'OTHER' && form.reason.trim().length === 0) {
      setError(t('billing.lifetimeFree.apply.errors.reasonRequiredOther'));
      return;
    }
    setSubmitting(true);
    try {
      const { valid } = await validateTaxIdCallable({
        country: form.countryCode,
        taxId: form.taxId.trim(),
      });
      if (!valid) {
        setError(t('tenants.create.errors.taxIdInvalid'));
        return;
      }
      setError(null);
      setConfirmOpen(true);
    } catch (err) {
      console.error(`${TAG}: validateTaxId failed`, err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): só `console.error` — invisível em produção. O report
      // leva o código e o erro, nunca o CNPJ/telefone digitados (o país basta para investigar).
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_courtesy_validate_tax_id_failed', {
          country: form.countryCode,
          code: err instanceof FirebaseError ? err.code : null,
          ...serializeError(err),
        });
      }
      setError(t('tenants.create.errors.taxIdCheckFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  /** Erro do `createCourtesyTenant` → texto traduzido para o `Alert` do modal (#1021). */
  function mapCreateError(err: unknown): string {
    if (err instanceof FirebaseError) {
      if (err.code === 'functions/already-exists') return t('tenants.create.errors.alreadyExists');
      if (err.code === 'functions/invalid-argument') return t('tenants.create.errors.invalidInput');
    }
    return t('tenants.create.errors.generic');
  }

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    const phoneE164 = PhoneFormatter.toE164(form.countryCode, form.digits);

    try {
      const result = await createCourtesyTenantCallable({
        tenantName: form.tenantName.trim(),
        tenantKind: form.tenantKind,
        countryCode: form.countryCode,
        taxId: form.taxId.trim(),
        phoneE164,
        locale: form.locale,
        timeZone: form.timeZone,
        address: form.address.trim(),
        category: form.category,
        reason: form.reason.trim(),
      });
      toast.success(t('tenants.create.success'));
      setConfirmOpen(false);
      setSubmitting(false);
      onCreated(result.tenantId);
    } catch (err) {
      console.error(`${TAG}: createCourtesyTenant failed`, err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): só `console.error` — invisível em produção. Sem os
      // dados do formulário no report (nome, CNPJ, telefone, endereço); código + erro bastam.
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_courtesy_create_tenant_failed', {
          country: form.countryCode,
          category: form.category,
          code: err instanceof FirebaseError ? err.code : null,
          ...serializeError(err),
        });
      }
      setConfirmOpen(false);
      setSubmitting(false);
      setError(mapCreateError(err));
    }
  }

  return (
    <>
      <Modal
        visible={open && !confirmOpen}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(submitting)) { onClose(); } }}
        header={t('tenants.create.title')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClose} disabled={submitting}>
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={submitting}
                disabled={!canSubmit}
                onClick={(): void => {
                  void requestConfirm();
                }}
              >
                {t('tenants.create.submit')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p" color="text-body-secondary">
            {t('tenants.create.description')}
          </Box>

          <FormField
            label={t('tenants.create.owner')}
            description={t('tenants.create.ownerHint')}
          >
            <Input value={ownerEmail} readOnly disabled onChange={(): void => undefined} />
          </FormField>

          <FormField label={t('tenants.create.name')}>
            <Input
              value={form.tenantName}
              disabled={submitting}
              placeholder={t('tenants.create.namePlaceholder')}
              onChange={({ detail }): void => {
                patch({ tenantName: detail.value });
              }}
            />
          </FormField>

          <FormField label={t('tenants.create.kind')}>
            <Select
              selectedOption={kindOptions.find((o) => o.value === form.tenantKind) ?? null}
              options={kindOptions}
              disabled={submitting}
              onChange={({ detail }): void => {
                patch({ tenantKind: detail.selectedOption.value as TenantKind });
              }}
            />
          </FormField>

          <FormField label={t('tenants.create.country')}>
            <Select
              selectedOption={countryOptions.find((o) => o.value === form.countryCode) ?? null}
              options={countryOptions}
              disabled={submitting}
              onChange={({ detail }): void => {
                changeCountry(detail.selectedOption.value as Country);
              }}
            />
          </FormField>

          <FormField
            label={t('tenants.create.phone')}
            constraintText={`${PhoneFormatter.dialCodeFor(form.countryCode)} ${phoneSpec.placeholder}`}
          >
            <Input
              value={phoneSpec.format(form.digits)}
              inputMode="tel"
              disabled={submitting}
              placeholder={phoneSpec.placeholder}
              onChange={({ detail }): void => {
                patch({
                  digits: PhoneFormatter.digitsOnly(detail.value).slice(0, phoneSpec.expectedDigits),
                });
              }}
            />
          </FormField>

          <FormField
            label={taxIdSpec.label}
            constraintText={taxIdSpec.placeholder}
            errorText={taxIdError ?? undefined}
          >
            <Input
              value={taxIdSpec.format(form.taxId)}
              disabled={submitting}
              placeholder={taxIdSpec.placeholder}
              invalid={taxIdError !== null}
              onChange={({ detail }): void => {
                changeTaxId(detail.value);
              }}
            />
          </FormField>

          <FormField label={t('tenants.create.locale')}>
            <Select
              selectedOption={localeOptions.find((o) => o.value === form.locale) ?? null}
              options={localeOptions}
              disabled={submitting}
              onChange={({ detail }): void => {
                patch({ locale: detail.selectedOption.value as TenantLocale });
              }}
            />
          </FormField>

          <FormField label={t('tenants.create.timeZone')}>
            <Select
              selectedOption={timeZoneOptions.find((o) => o.value === form.timeZone) ?? null}
              options={timeZoneOptions}
              disabled={submitting}
              onChange={({ detail }): void => {
                patch({ timeZone: String(detail.selectedOption.value) });
              }}
            />
          </FormField>

          <FormField label={t('tenants.create.address')}>
            <Input
              value={form.address}
              disabled={submitting}
              placeholder={t('tenants.create.addressPlaceholder')}
              onChange={({ detail }): void => {
                patch({ address: detail.value });
              }}
            />
          </FormField>

          <FormField label={t('billing.lifetimeFree.apply.categoryLabel')}>
            <RadioGroup
              value={form.category}
              onChange={({ detail }): void => {
                patch({ category: detail.value as LifetimeFreeCategory });
              }}
              items={COURTESY_GRANT_CATEGORIES.map((cat) => ({
                value: cat,
                label: t(`billing.lifetimeFree.category.${cat}`, { defaultValue: cat }),
              }))}
            />
          </FormField>

          <FormField
            label={t('billing.lifetimeFree.apply.reasonLabel')}
            constraintText={form.category === 'OTHER' ? '*' : undefined}
          >
            <Input
              value={form.reason}
              disabled={submitting}
              placeholder={t('billing.lifetimeFree.apply.reasonPlaceholder')}
              onChange={({ detail }): void => {
                patch({ reason: detail.value });
              }}
            />
          </FormField>
          {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
              do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('tenants.create.confirmTitle')}
        confirmLabel={t('tenants.create.confirmAction')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">
          {t('tenants.create.confirmDescription', {
            tenantName: form.tenantName.trim(),
            ownerEmail,
          })}
        </Box>
      </AdminConfirm>
    </>
  );
}
